import React, { useEffect, useState } from "react";
import { Button, message } from "antd";
import { ReloadOutlined, PlusOutlined, LogoutOutlined, CopyOutlined } from "@ant-design/icons";
import "./HeaderComponent.css";
import MaitaiLogo from "./assets/maitai_logo_light_transparent.svg";
import NewConversationModal from "./NewConversationModal";
import { useAuth0 } from "@auth0/auth0-react";

// Define an interface for the component props
interface HeaderComponentProps {
    onRefresh: () => void;
    onStartNewConversation: (systemPrompt: string, assistantFirst: boolean, summaryMode: boolean, context: string, applicationName?: string) => void;
    modelCategory?: string;
    conversation?: any;
    hasMessages?: boolean;
}

const HeaderComponent: React.FC<HeaderComponentProps> = ({ 
    onRefresh, 
    onStartNewConversation,
    modelCategory = "models",
    conversation,
    hasMessages = false,
}) => {
    const { logout } = useAuth0();
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [modalVisible, setModalVisible] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const formatConversation = () => {
        if (!conversation || !conversation.turns || conversation.turns.length === 0) {
            return "";
        }

        let formatted = "";
        
        conversation.turns.forEach((turn: any) => {
            turn.messages
                .filter((msg: any) => msg.message && msg.message.trim() !== "")
                .forEach((msg: any) => {
                    if (msg.sender === "USER") {
                        formatted += `[USER]:\t\t\t\t${msg.message}\n`;
                    } else if (msg.sender === "BOT") {
                        // Get timing info if available
                        const ttft = msg.meta?.ttft ? Math.round(msg.meta.ttft) : null;
                        const infTime = msg.meta?.inf_time ? Math.round(msg.meta.inf_time) : null;
                        
                        let timingInfo = "";
                        if (ttft && infTime) {
                            timingInfo = ` - ${ttft}/${ttft + infTime}ms`;
                        } else if (ttft) {
                            timingInfo = ` - ${ttft}ms`;
                        } else if (infTime) {
                            timingInfo = ` - ${infTime}ms`;
                        }
                        
                        formatted += `[BOT${timingInfo}]:\t${msg.message}\n`;
                    }
                });
        });

        return formatted;
    };

    const handleCopyConversation = async () => {
        try {
            const formattedConversation = formatConversation();
            if (formattedConversation.trim() === "") {
                message.warning("No conversation to copy");
                return;
            }
            
            await navigator.clipboard.writeText(formattedConversation);
            message.success("Conversation copied to clipboard!");
        } catch (error) {
            console.error("Failed to copy conversation:", error);
            message.error("Failed to copy conversation");
        }
    };

    return (
        <>
            <div className="header-container">
                <div className={`leftSection ${isMobile ? "mobile" : ""}`}>
                    <img 
                        src={MaitaiLogo} 
                        className="logo-image"
                        alt="Maitai Logo"
                    />
                </div>
                <div className="rightSection">
                    {hasMessages && (
                        <Button 
                            icon={<CopyOutlined />}
                            onClick={handleCopyConversation}
                            style={{ marginRight: "8px" }}
                            title="Copy conversation to clipboard"
                        >
                            Copy Conversation
                        </Button>
                    )}
                    <Button 
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setModalVisible(true)}
                        style={{ marginRight: "8px" }}
                    >
                        New Conversation
                    </Button>
                    <Button 
                        type="text"
                        icon={<ReloadOutlined />}
                        onClick={onRefresh}
                        title="Clear messages and start new session"
                    />
                    <Button
                        type="text"
                        icon={<LogoutOutlined />}
                        onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                        title="Sign Out"
                    />
                </div>
            </div>
            <NewConversationModal
                visible={modalVisible}
                onClose={() => setModalVisible(false)}
                onSubmit={onStartNewConversation}
                modelCategory={modelCategory}
            />
        </>
    );
};

export default HeaderComponent;
